import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { VerticalWrapper } from '@styles/global/helpers'
import { GridFluid } from '@styles/global/cssGrid'
import VerticalCategory from '@components/dividers/verticalCategory'
import { device } from '@styles/global/breakpoints'
import { ICourse, ICurriculum } from '@et/types/Courses'
import { renderSvg, scrollPageTo } from '@utils/genUtils'
import { svgs } from '@svg'
import animateScrollTo from 'animated-scroll-to'
import ThemeButton from '@components/buttons/ThemeButton'
import { useVideoStats } from '@hooks/useVideoStats'
import { motion } from 'framer-motion'

interface IProps {
  listBackgroundColor?: string
  listTextColor?: string
  listIconColor?: string
  outline?: boolean
  buttonOutlineColor?: string
  buttonHoverColor?: string
  showEnrollBtn?: boolean;
}

function Curriculum (props: ICourse & IProps) {
  const [open, setOpen] = useState(false)
  const { curriculum, title, showEnrollBtn = false } = props
  const { totalModules, totalVideos, totalMins } = useVideoStats(curriculum)
  const sectionA: ICurriculum[] = curriculum.slice(0, 3)
  const sectionB: ICurriculum[] = curriculum.slice(3, props.curriculum.length)
  const container: MutableRefObject<null | HTMLElement> = useRef(null)
  const sellCard: MutableRefObject<null | HTMLElement> = useRef(null)

  function toggleOpen () {
    setOpen(!open)
    if (open && container.current) {
      // @ts-ignore
      animateScrollTo(container.current, {
        // duration of the scroll per 1000px, default 500
        speed: 250,

        // minimum duration of the scroll
        minDuration: 250,

        // maximum duration of the scroll
        maxDuration: 500,

        // DOM element to scroll, default window
        // Pass a reference to a DOM object
        // Example: document.querySelector('#element-to-scroll'),
        element: window,

        // Additional offset value that gets added to the desiredOffset.  This is
        // useful when passing a DOM object as the desiredOffset and wanting to adjust
        // for an fixed nav or to add some padding.
        offset: 75,

        // should animated scroll be canceled on user scroll/keypress
        // if set to "false" user input will be disabled until animated scroll is complete
        // (when set to false, "passive" will be also set to "false" to prevent Chrome errors)
        cancelOnUserAction: true,

        // Set passive event Listeners to be true by default. Stops Chrome from complaining.
        passive: true,

        // Scroll horizontally rather than vertically (which is the default)
        // horizontal: true,

        // function that will be executed when the scroll animation is finished
        // onComplete: function() {});
      })
    }
  }

  useEffect(() => {
    container.current = document.getElementById('curriculum')
    sellCard.current = document.getElementById('mini-card')
  }, [])

  function handleClick () {
    if (showEnrollBtn) {
      scrollPageTo(sellCard)
    }
  }

  return (
    <VerticalWrapper id={`curriculum`}>
      <GridFluid>
        <VerticalCategory text={`Class Curriculum`}/>
        <Spacer row={`1`} margin={`40`}/>
        <CurriculumWrapper>
          <ClassTitle>
            <Stats>
              <li>{totalModules} Modules</li>
              <li>{totalVideos} Videos</li>
              <li>{totalMins} Hours</li>
            </Stats>
            <h2>{title}</h2>
          </ClassTitle>
          {/*Set arrow after first 3*/}
          {sectionA.map((section: ICurriculum, index: number) => (
            <CurriculumItem
              outline={props.outline}
              backgroundColor={props.listBackgroundColor}
              textColor={props.listTextColor}
              key={section.title}
              showBtn={showEnrollBtn}>
              <h5>Module {index + 1}</h5>
              <h3>{section.title}</h3>
              <ul>
                {section.videos && section.videos.map((video) => (
                  <li key={video.title} onClick={handleClick}>
                    <Icon iconColor={props.listIconColor}>{renderSvg(svgs.Youtube)}</Icon>
                    <VideoContent>
                      <Title>{video.title}</Title>
                      <Enroll>
                        <Time>{video.duration}</Time>
                        <EnrollBtn>Enroll Now!</EnrollBtn>
                      </Enroll>
                    </VideoContent>
                  </li>
                ))}
              </ul>
            </CurriculumItem>
          ))}
          {/*Rest of videos*/}
          <FullCurriculum
            initial={'closed'}
            variants={curriculumVariants}
            animate={open ? 'open' : 'close'}>
            {sectionB.map((section: ICurriculum, index: number) => {
              return (
                <CurriculumItem
                  outline={props.outline}
                  backgroundColor={props.listBackgroundColor}
                  textColor={props.listTextColor}
                  key={section.title}
                  showBtn={showEnrollBtn}>
                  <h5>Module {index + 4}</h5>
                  <h3>{section.title}</h3>
                  <ul>
                    {section.videos && section.videos.map((video) => (
                      <li key={video.title} onClick={handleClick}>
                        <Icon iconColor={props.listIconColor}>{renderSvg(svgs.Youtube)}</Icon>
                        <VideoContent>
                          <Title>{video.title}</Title>
                          <Enroll>
                            <Time>{video.duration}</Time>
                            <EnrollBtn>Enroll Now!</EnrollBtn>
                          </Enroll>
                        </VideoContent>
                      </li>
                    ))}
                  </ul>
                </CurriculumItem>
              )
            })}
          </FullCurriculum>

          <ThemeButton
            outlineColor={props.buttonOutlineColor}
            outlineHoverColor={props.buttonHoverColor}
            clickHandler={toggleOpen}
            text={open ? 'Close' : `View all`}
            outline={true}/>
          {/*open btn*/}
        </CurriculumWrapper>
        <Spacer row={`3`}/>
      </GridFluid>
    </VerticalWrapper>
  )
}

interface ISpacerProps {
  row: string
  margin?: string
}

const VideoContent = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 0 15px; 
  flex: 1;
  @media ${device.tablet}{
    margin: 0 15px;
    flex-direction: row; 
  }
`
const EnrollBtn = styled.button`
  position: absolute;
  right: 0;
  outline: none;
  border: 3px solid #fff;
  background: transparent;
  text-align: center;
  color: #fff;
  border-radius: 25px;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 12px;
  padding: 6px 11px;
  width: 100%;
  top: 50%;
  transform: translateY(75px);
  transition: .3s;
  cursor: pointer;
  
  &:focus{
    outline: none;
  }
`
const Time = styled.div`
  transition: .3s;
  font-weight: 600;
  font-size: 14px;
  text-align: left;
  @media ${device.tablet}{
      text-align: right; 
  } 
  @media ${device.laptop}{
    text-align: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  } 
`
const Enroll = styled.div`
  position: relative;
  width: 120px;
`
const ClassTitle = styled.div`
  color: ${props => props.theme.headlineColor};
  width: 100%;
  margin: 0 0 40px 80px;
  h2{
    font-size: 32px;
    line-height: 38px;
    margin: 0;    	
  }
  
  @media ${device.laptop} {
    margin: 0 0 60px 80px;
    h2{
      font-size: 40px;
    }        
  }
`
const Stats = styled.ul`
  color: ${props => props.theme.highlight.i500};
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  li{
    padding-right: 15px;
    font-weight: 600;
  }
  
  @media ${device.tablet}{
     flex-direction: row;
  } 
`

const curriculumVariants = {
  open: {
    height: 'auto',
    transition: {
      duration: .4,
      ease: 'easeInOut',
    },
  },
  close: {
    height: 0,
    transition: {
      duration: .5,
      delay: .3,
      ease: 'easeInOut',
    },
  },
}
const FullCurriculum = styled(motion.div)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 0;
`
const CurriculumWrapper = styled.div`
  grid-column: 2 / 4;
  margin: 0 20px 0 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
   @media ${device.tablet}{
    margin: 0;
    grid-column: 3/ 13;
   }
   @media ${device.laptop}{
    margin: 0;
    grid-column: 4/ 12;
   }
`
const Icon = styled.div<{ iconColor: string | undefined }>`
  width: 20px;
  height: 20px;
  svg{
    path{
      transition: .3s;
      fill: ${props => props.iconColor ? props.iconColor : props.theme.highlight.i500};
    }
  }
`
const Title = styled.div`
  flex: 1;
  text-align: left;
  transition: .3s;
  margin-bottom: 10px;
    @media ${device.tablet}{
      margin-bottom: 0; 
    }
`
const CurriculumItem = styled.div<{
  outline: boolean | undefined,
  showBtn: boolean,
  backgroundColor: string | undefined,
  textColor: string | undefined
}>`
  margin-bottom: 50px;
  width: 100%;
  overflow: visible;
  h3{
    margin-left: 40px;
    font-size: 21px;
    font-weight: 700;
    color: ${props => props.theme.headlineColor};
    margin-bottom: 15px;
  }
  h5{
    margin-left: 40px;
    text-transform: uppercase;
    line-height: 13px;
    color: ${props => props.theme.subheaderColor};
  }
  
  ul{
    margin: 0;
    padding: 0;
    background: ${props => props.backgroundColor ? props.backgroundColor : props.theme.backgroundSecondary};
    border-radius: 10px;
    overflow: hidden;
    ${props =>
  props.outline
    ? `border: 3px solid ${props.theme.outline}`
    : `box-shadow: 0px 4px 40px rgba(0, 0, 0, 0.25);`
  }
  }
  
  li{
    padding: 18px 15px;
    color: ${props => props.textColor ? props.textColor : props.theme.textColor};
    list-style: none;
    font-weight: 500;
    display: flex;
    flex-direction: row;
    line-height: 20px;
    background: transparent;
    transition: .3s;
    cursor: ${props => props.showBtn ? 'pointer' : 'default'};
    overflow: hidden;
    
    @media ${device.tablet}{
      padding: 18px 40px;
      align-items: center; 
      justify-content: center;
    }
    @media ${device.laptop}{
      padding: 18px 0 18px 40px;
      ${props => props.showBtn ? `
        &:hover{
          background: ${props.theme.buttonPrimary.backgroundHoverColor};
          ${Icon}{
            svg{
              path{
                fill: #fff;
              }
            }
          };
          ${Title}, ${Time}{
            color: #fff;
          }
          ${Time}{
            transform: translateX(-50%) translateY(-75px);
          }
          ${EnrollBtn}{
            transform: translateY(-50%);
          }
        }  
      ` : ''}
    }
  }
`
const Spacer = styled.div<ISpacerProps>`
  margin-top: 40px;
  grid-row: ${props => props.row};  
  grid-column: -1 / 1;
  @media ${device.tablet}{
    grid-row: ${props => props.row};
    grid-column: 4 / 14;
    margin-top: 40px;  
  }
  
  @media ${device.laptop}{
    margin-top: ${props => props.margin ? props.margin + `px` : `80px`};  
  }
`

export default Curriculum
